import type { CrossLink, DocId, MapNode, NodeId, NodeMap, TagMap } from './types';
import { DOC_ID_FIELD_BY_KEY, DOC_KINDS, PIPELINE_DISPLAY, pipelineStageOf } from './docs';
import { isExemptPlan } from './health';

/**
 * The documentation coverage dashboard.
 *
 * Two functions that count the same map differently, on purpose:
 *
 *   computeDashboardStats  ABSOLUTE counts. Every node the user can see, unfiltered.
 *   computeCompletionStats COVERAGE percentages, which EXCLUDE exempt nodes (plan = {}).
 *
 * The split is the whole point. A node deliberately marked as never needing documentation
 * would otherwise drag the coverage percentage down for ever, and no amount of work would
 * move it — so the pies exclude those while the tallies keep counting them.
 */

export interface DashboardStats {
  total: number;
  withTags: number;
  withDesc: number;
  /** No tags, no description, no documents at all. */
  bare: number;
  /** Carries all four fixed slots. */
  fullyDocumented: number;
  byKind: Record<string, number>;
  withCustomDocs: number;
  /** Attachments, so one page linked from three nodes counts three times. */
  customDocAttachments: number;
  /** …and distinct pages, so the same case counts once. */
  distinctCustomDocPages: number;
  byPipelineStage: Record<string, number>;
  tagAssignCount: number;
  avgTagsPerNode: number;
  usedTagCount: number;
  definedTagCount: number;
  depthCounts: Record<number, number>;
  collapsed: number;
  crossLinks: number;
}

export interface StatsInput {
  nodes: NodeMap;
  tags: TagMap;
  links: readonly CrossLink[];
  isPermHidden: (id: NodeId) => boolean;
  /** Pipeline-stage counts read the prompt-scoped `pipelineStage`; zero without the data. */
  canAiAdmin: boolean;
}

export function computeDashboardStats(input: StatsInput): DashboardStats {
  const ids = Object.keys(input.nodes).filter((id) => !input.isPermHidden(id));
  const total = ids.length;

  const stats: DashboardStats = {
    total,
    withTags: 0,
    withDesc: 0,
    bare: 0,
    fullyDocumented: 0,
    byKind: Object.fromEntries(DOC_KINDS.map((k) => [k.key, 0])),
    withCustomDocs: 0,
    customDocAttachments: 0,
    distinctCustomDocPages: 0,
    byPipelineStage: Object.fromEntries(PIPELINE_DISPLAY.map((v) => [v.key, 0])),
    tagAssignCount: 0,
    avgTagsPerNode: 0,
    usedTagCount: 0,
    definedTagCount: Object.keys(input.tags).length,
    depthCounts: {},
    collapsed: 0,
    crossLinks: input.links.length,
  };

  const customDocSet = new Set<DocId>();

  for (const id of ids) {
    const n: MapNode = input.nodes[id];
    const nTags = (n.tags ?? []).length;
    if (nTags > 0) stats.withTags++;
    stats.tagAssignCount += nTags;

    const hasDesc = !!n.desc?.trim();
    if (hasDesc) stats.withDesc++;

    let docCount = 0;
    for (const k of DOC_KINDS) {
      if (n[DOC_ID_FIELD_BY_KEY[k.key]]) {
        stats.byKind[k.key]++;
        docCount++;
      }
    }
    if (docCount === DOC_KINDS.length) stats.fullyDocumented++;

    const custom = n.customDocs ?? [];
    if (custom.length) {
      stats.withCustomDocs++;
      stats.customDocAttachments += custom.length;
      for (const d of custom) customDocSet.add(d);
    }

    if (nTags === 0 && !hasDesc && docCount === 0 && custom.length === 0) stats.bare++;
    if (n.collapsed) stats.collapsed++;

    const depth = n.depth || 0;
    stats.depthCounts[depth] = (stats.depthCounts[depth] ?? 0) + 1;

    if (input.canAiAdmin) {
      const stage = pipelineStageOf(n);
      if (stage) stats.byPipelineStage[stage] = (stats.byPipelineStage[stage] ?? 0) + 1;
    }
  }

  stats.distinctCustomDocPages = customDocSet.size;
  stats.avgTagsPerNode = total ? stats.tagAssignCount / total : 0;
  stats.usedTagCount = new Set(ids.flatMap((id) => input.nodes[id].tags ?? [])).size;
  return stats;
}

export interface CoverageSlice {
  count: number;
  total: number;
  /** Rounded to a whole percent, because it is rendered inside a 64px pie. */
  pct: number;
}

export interface CompletionStats {
  /** Nodes IN SCOPE — visible, and not exempt (plan = {}, "nothing expected"). */
  total: number;
  byKind: Record<string, CoverageSlice>;
  tagsStat: CoverageSlice;
  /** How many visible nodes were excluded for being exempt (plan = {}). */
  excluded: number;
}

const pct = (count: number, total: number): number => (total ? Math.round((count / total) * 100) : 0);

export function computeCompletionStats(input: Omit<StatsInput, 'canAiAdmin' | 'links'>): CompletionStats {
  // Exclude EXEMPT nodes — those whose plan says "nothing expected here" (expectedDocs = {}),
  // the successor to the retired "No doc" tag (V0.42). This now agrees with the heatmap's
  // `slate` bucket instead of duplicating a tag-name match.
  const allIds = Object.keys(input.nodes).filter((id) => !input.isPermHidden(id));
  const ids = allIds.filter((id) => !isExemptPlan(input.nodes[id].expectedDocs));
  const total = ids.length;

  const byKind: Record<string, CoverageSlice> = {};
  for (const k of DOC_KINDS) {
    const count = ids.filter((id) => input.nodes[id][DOC_ID_FIELD_BY_KEY[k.key]]).length;
    byKind[k.key] = { count, total, pct: pct(count, total) };
  }

  const tagged = ids.filter((id) => (input.nodes[id].tags ?? []).length > 0).length;

  return {
    total,
    byKind,
    tagsStat: { count: tagged, total, pct: pct(tagged, total) },
    excluded: allIds.length - total,
  };
}
